import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ErrorEntity } from '@p2p/shared/domain';
import { transformStringPath } from '@p2p/shared/utils';
import _ from 'lodash';

import { restrictionsKnown } from '../constants/restrictions';
import {
    PaymentDirections,
    PaymentMethodsPreassignedResponseMethod,
    PaymentMethodsSettingsResponseCodes,
    PaymentProfile,
    PaymentProfileRestriction,
    PaymentProfileRestrictionSettingsCodes,
    PaymentRequisite,
    PaymentTypes,
    RequisiteSchema,
    RequisiteSchemaEntry,
    RequisiteSchemaValidations
} from '../entities';
import { getRequisitesSchemaByPaymentType } from '../utils';

export enum PaymentProfileFormControls {
    Id = 'id',
    Title = 'title',
    PaymentMethodId = 'paymentMethodId',
    Requisites = 'requisites',
    RestrictionSettings = 'restrictionSettings'
}

export const requisitesPaymentTypeControlName = 'paymentType';

const paymentMethodsRestrictionsMap: Record<string, PaymentMethodsSettingsResponseCodes> = {
    [PaymentProfileRestrictionSettingsCodes.DailyLimit]: PaymentMethodsSettingsResponseCodes.DAILY_LIMIT
};

export class PaymentProfileForm {
    readonly requisitesFormGroup = new FormGroup<any>({});
    readonly restrictionsFormArray = new FormArray<any>([]);
    readonly formGroup = new FormGroup<any>({
        [PaymentProfileFormControls.Title]: new FormControl('', [Validators.required]),
        [PaymentProfileFormControls.PaymentMethodId]: new FormControl(null, [Validators.required]),
        [PaymentProfileFormControls.Requisites]: this.requisitesFormGroup,
        [PaymentProfileFormControls.RestrictionSettings]: this.restrictionsFormArray
    });

    private paymentMethodsData: PaymentMethodsPreassignedResponseMethod[] = [];
    private paymentRestrictionsData: PaymentProfileRestriction[] = [];

    private _restrictionsList: PaymentProfileRestriction[] = [];
    private _restrictionsListToShow: PaymentProfileRestriction[] = [];
    private _selectedPaymentMethod: PaymentMethodsPreassignedResponseMethod | null = null;
    private _restrictionsValidatorsDataMap: Record<string, Record<string, string>> = {
        [PaymentProfileRestrictionSettingsCodes.DailyLimit]: {
            max: '0'
        }
    };

    private _currentRequisitesSchema: RequisiteSchema | null = null;

    get restrictionsListToShow(): PaymentProfileRestriction[] {
        return this._restrictionsListToShow;
    }

    get selectedPaymentMethod(): PaymentMethodsPreassignedResponseMethod | null {
        return this._selectedPaymentMethod;
    }

    getRequisiteType(key: string): string {
        return this._currentRequisitesSchema ? this._currentRequisitesSchema?.[key].type : 'string';
    }

    getRestrictionValidatorData(restriction: PaymentProfileRestrictionSettingsCodes): Record<string, string> {
        return this._restrictionsValidatorsDataMap[restriction];
    }

    setMetaData(
        paymentMethods: PaymentMethodsPreassignedResponseMethod[],
        restrictions: PaymentProfileRestriction[]
    ): void {
        this.paymentMethodsData = paymentMethods;
        this.paymentRestrictionsData = restrictions.filter((restriction) =>
            restrictionsKnown.includes(restriction.code)
        );
    }

    setFormData(profile: PaymentProfile): void {
        this.formGroup.addControl(PaymentProfileFormControls.Id, new FormControl(profile.id), { emitEvent: false });
        this.formGroup
            .get(PaymentProfileFormControls.PaymentMethodId)
            ?.setValue(profile.paymentMethodId, { emitEvent: false });
        this.formGroup.get(PaymentProfileFormControls.PaymentMethodId)?.disable({ emitEvent: false });
        this.formGroup.get(PaymentProfileFormControls.Title)?.setValue(profile.title, { emitEvent: false });
        this.handlePaymentMethodSelection(profile.paymentMethodId, profile.requisites);

        profile.restrictionSettings.forEach((restriction) => {
            this.addRestriction(restriction.code, Number(restriction.value).toString());
        });
    }

    handlePaymentMethodSelection(paymentMethodId: number, requisites?: PaymentRequisite): void {
        const paymentMethod = this.paymentMethodsData.find((method) => method.id === paymentMethodId);
        const paymentType = paymentMethod?.paymentType;

        this._restrictionsList = this.paymentRestrictionsData.filter(
            (restriction) => restriction.direction === paymentMethod?.direction
        );
        this._restrictionsListToShow = [...this._restrictionsList];
        this._selectedPaymentMethod = paymentMethod ? paymentMethod : null;

        if (paymentType) {
            this.handlePaymentMethodRequisites(paymentType, paymentMethod, requisites);
        }
    }

    addRestriction(restrictionToAdd: PaymentProfileRestrictionSettingsCodes, value?: string): void {
        this._restrictionsListToShow = this._restrictionsListToShow.filter(
            (restrictionToShow) => restrictionToShow.code !== restrictionToAdd
        );

        this.restrictionsFormArray.push(
            new FormGroup({
                code: new FormControl(restrictionToAdd),
                value: this.getRestrictionSettingFormControl(restrictionToAdd, value)
            }),
            { emitEvent: false }
        );
    }

    removeRestriction(restrictionToRemove: PaymentProfileRestrictionSettingsCodes): void {
        const restriction = this._restrictionsList.find(
            (restrictionToFind) => restrictionToFind.code === restrictionToRemove
        );

        this._restrictionsListToShow = restriction
            ? [...this._restrictionsListToShow, restriction]
            : this._restrictionsListToShow;

        let targetIndex: number | null = null;

        for (let i = 0; i < this.restrictionsFormArray.controls.length; i++) {
            const formGroup = this.restrictionsFormArray.controls[i] as FormGroup;

            if (formGroup.get('code')?.value === restrictionToRemove) {
                targetIndex = i;
                break;
            }
        }

        if (targetIndex !== null) {
            this.restrictionsFormArray.removeAt(targetIndex, { emitEvent: false });
        }
    }

    validateForm(): void {
        this.markForValidation(this.formGroup);
        _.values(this.formGroup.controls).forEach((control) => this.markForValidation(control));
        _.values(this.restrictionsFormArray.controls).forEach((control) => {
            const formGroup = control as FormGroup;

            this.markForValidation(control);
            _.values(formGroup.controls).forEach((groupControl) => this.markForValidation(groupControl));
        });
        _.values(this.requisitesFormGroup.controls).forEach((control) => this.markForValidation(control));
    }

    setFieldError(entity: ErrorEntity): void {
        this.setFieldErrorByPath(this.formGroup, entity.code, transformStringPath(entity.field ?? ''), 0);
    }

    getFormRawValue(): any {
        const formData = this.formGroup.getRawValue();

        const requisites = _.keys(formData[PaymentProfileFormControls.Requisites]).reduce((acc, cur) => {
            if (this._currentRequisitesSchema?.[cur]?.type === 'number') {
                acc[cur] = Number(formData[PaymentProfileFormControls.Requisites][cur]);
            } else {
                acc[cur] = formData[PaymentProfileFormControls.Requisites][cur];
            }

            return acc;
        }, {} as any);

        const result = { ...formData };

        if (formData[PaymentProfileFormControls.Requisites]) {
            result[PaymentProfileFormControls.Requisites] = requisites;
        }

        return result;
    }

    private handlePaymentMethodRequisites(
        paymentType: PaymentTypes,
        paymentMethod: PaymentMethodsPreassignedResponseMethod,
        requisites?: PaymentRequisite
    ): void {
        const schema = getRequisitesSchemaByPaymentType(paymentType);

        this._currentRequisitesSchema = schema;

        _.keys(this.requisitesFormGroup.controls).forEach((controlKey) => {
            this.requisitesFormGroup.removeControl(controlKey, { emitEvent: false });
        });

        this.formGroup.removeControl(PaymentProfileFormControls.Requisites, { emitEvent: false });

        if (paymentMethod?.direction === PaymentDirections.PAY_IN) {
            this.formGroup.addControl(PaymentProfileFormControls.Requisites, this.requisitesFormGroup, {
                emitEvent: false
            });

            _.keys(schema).forEach((schemaKey) => {
                const requisiteValue = this.getRequisiteValue(schema, schemaKey, requisites);
                const validation = this.getMethodRequisiteValidation(schema[schemaKey]);

                this.requisitesFormGroup.addControl(schemaKey, new FormControl(requisiteValue, validation), {
                    emitEvent: false
                });
            });

            this.requisitesFormGroup.addControl(requisitesPaymentTypeControlName, new FormControl(paymentType), {
                emitEvent: false
            });
        }

        this.restrictionsFormArray.clear();
    }

    private getMethodRequisiteValidation(schemaEntry: RequisiteSchemaEntry): ValidatorFn[] {
        const result: ValidatorFn[] = [];

        if (schemaEntry.validation.includes(RequisiteSchemaValidations.REQUIRED)) {
            result.push(Validators.required);
        }

        if (schemaEntry.validation.includes(RequisiteSchemaValidations.EMAIL)) {
            result.push(Validators.email);
        }

        return result;
    }

    private getRequisiteValue(schema: RequisiteSchema, schemaKey: string, requisites?: PaymentRequisite) {
        if (!requisites) {
            return schema[schemaKey].type === 'string' ? '' : null;
        }

        return requisites[schemaKey as keyof PaymentRequisite];
    }

    private setFieldErrorByPath(form: FormGroup, error: string, path: string[], idx: number) {
        if (idx > path.length - 1) {
            form.setErrors({ [error]: true });

            return;
        }

        this.setFieldErrorByPath(form.controls[path[idx]] as FormGroup, error, path, idx + 1);
    }

    private getRestrictionSettingFormControl(
        restriction: PaymentProfileRestrictionSettingsCodes,
        value?: string
    ): FormControl {
        if (value) {
            return new FormControl(value, [
                Validators.required,
                ...this.getAndHandleRestrictionSettingFormControlValidators(restriction)
            ]);
        }

        const paymentMethodSetting = paymentMethodsRestrictionsMap[restriction];

        if (paymentMethodSetting) {
            const setting = this._selectedPaymentMethod?.paymentMethodSettings?.find(
                (setting) => setting.code === paymentMethodSetting
            );

            return new FormControl(setting?.value ?? '', [
                Validators.required,
                ...this.getAndHandleRestrictionSettingFormControlValidators(restriction)
            ]);
        }

        return new FormControl('', [Validators.required]);
    }

    private getAndHandleRestrictionSettingFormControlValidators(
        restriction: PaymentProfileRestrictionSettingsCodes
    ): ValidatorFn[] {
        const paymentMethodSetting = paymentMethodsRestrictionsMap[restriction];
        const setting = this._selectedPaymentMethod?.paymentMethodSettings?.find(
            (setting) => setting.code === paymentMethodSetting
        );

        if (restriction === PaymentProfileRestrictionSettingsCodes.DailyLimit && setting) {
            this._restrictionsValidatorsDataMap[restriction] = {
                ...this._restrictionsValidatorsDataMap[restriction],
                max: Number(setting.value).toString()
            };
            return [Validators.max(Number(setting.value))];
        }

        return [];
    }

    private markForValidation(control: AbstractControl): void {
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
    }
}
